<template>
  <div class="md:mb-2 md:mr-2 h-72">
    <o-scroll ref="scroll" class="w-full h-full bg-white border rounded-xl shadow dark:bg-gray-900">
      <template v-if="availability && !loading">
        <div v-for="(day, dayIndex) in availability.dates" :key="dayIndex">
          <template v-if="day.availability">
            <p class="m-3 pb-3 text-gray-600 text-sm border-b dark:text-gray-300">
              {{ $t('components.reservation.availability.auto.controller.for', { date: day.dateLabel }) }}
            </p>
            <template v-for="(slot, slotKey) in day.slots" :key="slotKey">
              <auto-slot
                :ref="`slot-${slot.start}`"

                v-model="_slot"
                :item="slot"
                :move="move"
              />
            </template>
          </template>
          <template v-else>
            <p class="p-3 text-gray-700 text-sm dark:text-gray-200">
              {{ $t('components.reservation.availability.auto.controller.no', { date: day.dateLabel }) }}
            </p>

            <div v-if="allEmployeeAvailability" class="mt-2 p-3 border-t">
              <p class="text-sm text-gray-600 mb-2 dark:text-gray-300">
                {{ $t('components.reservation.availability.auto.controller.other') }}
              </p>

              <o-button flat outline @click="removeEmployees">
                {{ $t('components.reservation.availability.auto.controller.show') }}
              </o-button>
            </div>

            <div class="p-3 border-t">
              <o-button flat outline :loading="checking" @click="nextAvailable">
                {{ $t('components.reservation.availability.auto.controller.next') }}
              </o-button>
            </div>
          </template>
        </div>
      </template>

      <div v-if="loading" class="w-full flex justify-center">
        <div class="flex flex-col items-center p-8">
          <o-loader size="20px" />
          <p class="text-sm text-gray-600 mt-2 dark:text-gray-300">
            {{ $t('components.reservation.availability.auto.controller.loading') }}
          </p>
        </div>
      </div>

      <div v-if="!availability && !loading" class="w-full flex justify-center">
        <div class="flex flex-col items-center p-8">
          <p class="text-sm text-gray-600 mt-2 dark:text-gray-300">
            {{ $t('components.reservation.availability.auto.controller.wrong') }}
          </p>
        </div>
      </div>
    </o-scroll>
  </div>
</template>

<script setup lang="ts">
import hash from 'object-hash'
import AutoSlot from '@/components/reservation/availability/auto/Slot.vue'

import { AvailabilityRequest, AvailabilityRequestDateMode, AvailabilityRequestGroupMode } from '@/_api/models/booking'

const { $api, $date, $notification, $i18n } = useNuxtApp()

interface Props {
  availabilitySlot?: object | null
  services: any[]
  // `null` happens transiently during user interactions (e.g., between
  // clearing availability and picking a new date). `fetchAvailability`
  // already falls back to `new Date()` when props.date is null.
  date: Date | null
  move?: Record<string, any> | null
  groupMode?: any
  startTime?: number | string | null
}

interface Emits {
  (e: 'update:availabilitySlot', value: any): void
  (e: 'update:loading', value: boolean): void
  (e: 'update:date', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  availabilitySlot: null,
  move: null,
  groupMode: () => AvailabilityRequestGroupMode.reservation,
  startTime: null,
})

const emit = defineEmits<Emits>()

const availability = ref<any>(null)
const allEmployeeAvailability = ref<boolean>(false)
const checking = ref<any>(null)
const loading = ref(true)
const timer = ref<any>(false)
const scroll = ref<any>()
let latestAvailabilityRequest = 0

const _slot = computed({
  get () {
    return props.availabilitySlot
  },
  set (value) {
    emit('update:availabilitySlot', value)
  },
})

watch(() => props.date, () => {
  getSelectedEmployeeAvailability()
})

watch(() => props.services, () => {
  clearTimeout(timer.value)

  timer.value = setTimeout(() => {
    getSelectedEmployeeAvailability()
  }, 1000)
}, { deep: true })

watch(availability, () => {
  const avail = availability.value
  const st = (_slot.value as any)?.start || props.startTime

  if (st && avail?.dates?.length && avail.dates[0].availability) {
    const match = avail.dates[0].slots
      .find((slot: any) => {
        const difference = slot.start - st
        const deviation = 15

        return difference > -1 && difference < deviation
      })

    if (match) {
      _slot.value = match
      nextTick(() => {
        const element = document.querySelector(`[data-slot-start="${match.start}"]`) as HTMLElement
        if (element && scroll.value) {
          scroll.value.scroll(element)
        }
      })

      return
    }
  }

  _slot.value = null
})

watch(loading, (value) => {
  emit('update:loading', value)
})

async function getSelectedEmployeeAvailability () {
  const requestId = ++latestAvailabilityRequest
  loading.value = true
  allEmployeeAvailability.value = false

  try {
    const avail = await fetchAvailability()

    if (requestId !== latestAvailabilityRequest) return

    availability.value = avail
    loading.value = false

    if (avail && !avail.dates?.[0]?.availability) {
      getAllEmployeeAvailability(requestId)
    }
  } catch (e) {
    if (requestId !== latestAvailabilityRequest) return

    console.error(e)
    loading.value = false
  }
}

async function getAllEmployeeAvailability (requestId: number) {
  try {
    const avail = await fetchAvailability(false)

    if (requestId !== latestAvailabilityRequest) return

    if (avail?.dates?.length) {
      allEmployeeAvailability.value = avail.dates[0].availability
    }
  } catch (e) {
    if (requestId !== latestAvailabilityRequest) return

    console.error(e)
    loading.value = false
  }
}

async function fetchAvailability (includeEmployees = true) {
  const date = $date.getStartOfDayUTC(props.date || new Date())
  const dates = [date]

  const services = props.services.filter(({ service }: any) => service)

  const reservation: any = {
    services,
  }

  if (props.move) {
    reservation.idReservation = props.move.idReservation
  }

  const groupMode = props.groupMode

  const options = {
    includeEmployees,
    groupMode,
  }

  const request = AvailabilityRequest.forReservation(reservation, dates, options)

  request.dateMode = AvailabilityRequestDateMode.all
  request.includeSlots = true

  // Unwrap `.data` — `$api.booking.availability` returns the full
  // ApiResponse wrapper; callers below expect the AvailabilityResponse
  // itself (with its `.dates` array). Previously they read `response.dates`
  // off the wrapper and always got undefined, so no slots ever showed.
  const response = await $api.booking.availability(request)
  return response?.data ?? null
}

async function nextAvailable () {
  checking.value = true

  try {
    const dateStart = $date.startOf(props.date, 'day')

    const dates: any[] = []

    let day = $date.getStartOfDayUTC(dateStart)

    while (dates.length < 90) {
      day = $date.add(day, { days: 1 })
      dates.push(day)
    }

    const services = props.services.filter(({ service }: any) => service)

    const reservation: any = {
      services,
    }

    if (props.move) {
      reservation.idReservation = props.move.idReservation
    }

    const groupMode = props.groupMode

    const options = {
      includeEmployees: true,
      groupMode,
    }

    const request = AvailabilityRequest.forReservation(reservation, dates, options)

    request.dateMode = AvailabilityRequestDateMode.onePerDay
    request.includeSlots = false

    // Same ApiResponse unwrap as fetchAvailability above.
    const response = await $api.booking.availability(request)
    const data = response?.data

    if (data?.dates) {
      for (let i = 0; i < data.dates.length; i++) {
        const d = data.dates[i]

        if (d.availability) {
          emit('update:date', $date.utc(d.date))

          checking.value = null

          return
        }
      }
    }
  } catch (e) {
    checking.value = null
    $notification.error({ text: $i18n.t('components.reservation.availability.auto.controller.error') })

    return true
  }

  $notification.toast({
    title: $i18n.t('components.reservation.availability.auto.controller.success'),
    text: $i18n.t('components.reservation.availability.auto.controller.successInfo'),
    variant: 'warning',
  })

  checking.value = null
}

function removeEmployees () {
  const services = props.services

  for (const service of services) {
    service.employee = 0
  }
}

onMounted(() => {
  getSelectedEmployeeAvailability()
})

onBeforeUnmount(() => {
  clearTimeout(timer.value)
  latestAvailabilityRequest += 1

  if (loading.value) {
    emit('update:loading', false)
  }
})
</script>
